import React, { useEffect, useState, useRef } from "react";
import { Box, Card, Typography, Skeleton, Button, IconButton } from "@mui/material";
import { Briefcase, MapPin, DollarSign, Clock, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../../context/AuthContext";

const FeaturedJobs = () => {
  const { user } = useAuth();
  const userId = user?.user_id || user?.id;
  const [jobs, setJobs] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  const scrollLeft = () => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: -260, behavior: 'smooth' });
  };

  const scrollRight = () => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: 260, behavior: 'smooth' });
  };

  useEffect(() => {
    fetch("https://apiweb.hitpoly.com/ajax/communityWidgetsController.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "getFeaturedJobs" })
    })
      .then(r => r.json())
      .then(d => {
        if (d.success && d.data && d.data.length > 0) {
          setJobs(d.data);
        } else {
          setJobs([
            { id: 'm1', title: 'Media Buyer Senior - Scalability Expert', ubicacion: 'remoto • Perú', moneda: 'USD', salario_min: 1500, salario_max: 3500, tipo_contrato: 'tiempo completo' },
            { id: 'm2', title: 'Fullstack Developer (Node.js & React)', ubicacion: 'híbrido • Bogotá', moneda: 'USD', salario_min: 2500, salario_max: 4500, tipo_contrato: 'tiempo completo' }
          ]);
        }
      })
      .catch(() => {
        setJobs([
          { id: 'm1', title: 'Media Buyer Senior - Scalability Expert', ubicacion: 'remoto • Perú', moneda: 'USD', salario_min: 1500, salario_max: 3500, tipo_contrato: 'tiempo completo' },
          { id: 'm2', title: 'Fullstack Developer (Node.js & React)', ubicacion: 'híbrido • Bogotá', moneda: 'USD', salario_min: 2500, salario_max: 4500, tipo_contrato: 'tiempo completo' }
        ]);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Skeleton variant="rectangular" height={220} sx={{ mb: 2, borderRadius: 4 }} />;
  if (!jobs || jobs.length === 0) return null;

  return (
    <Card sx={{ 
      mb: 3, 
      bgcolor: "rgba(255,255,255,0.9)", 
      backdropFilter: "blur(8px)", 
      borderRadius: 4, 
      border: '1px solid #f0f0f0',
      boxShadow: 'none',
      p: 2.5
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="800" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#1a1a1a' }}>
          <Briefcase size={18} color="#0a66c2" /> Vacantes Abiertas
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" onClick={scrollLeft} sx={{ bgcolor: '#f0f2f5', width: 28, height: 28 }}><ChevronLeft size={16} /></IconButton>
          <IconButton size="small" onClick={scrollRight} sx={{ bgcolor: '#f0f2f5', width: 28, height: 28 }}><ChevronRight size={16} /></IconButton>
        </Box>
      </Box>

      <Box ref={scrollRef} sx={{ 
        display: 'flex', 
        gap: 2, 
        overflowX: 'auto', className: 'community-wall-scroll', scrollbarWidth: 'thin', scrollbarColor: '#0B8DB5 transparent', 
        pb: 1.5,
        mx: -0.5,
        px: 0.5,
        '&::-webkit-scrollbar': { height: 4 },
        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
        '&::-webkit-scrollbar-thumb': { bgcolor: '#0B8DB5', borderRadius: 4, border: 0 }
      }}>
        {jobs.map(job => (
          <Card key={job.id} sx={{ 
            minWidth: 260, 
            maxWidth: 260,
            p: 2, 
            borderRadius: 3, 
            border: '1px solid #eef3f8',
            boxShadow: 'none',
            bgcolor: '#ffffff',
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: '#0a66c2',
              boxShadow: '0 4px 12px rgba(10, 102, 194, 0.08)'
            }
          }} onClick={() => navigate(`/systems/bolsa/anuncio/${job.id}?userId=${userId}`)}>
            <Typography variant="body2" fontWeight="800" color="#0a66c2" sx={{ mb: 1.5, lineHeight: 1.3 }}>
              {job.title}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 500 }}>
                <MapPin size={12} /> {job.ubicacion || 'Remoto'}
              </Typography>
              <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
                <DollarSign size={12} /> {job.moneda || 'USD'} {job.salario_min} - {job.salario_max}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 500 }}>
                <Clock size={12} /> {job.tipo_contrato || 'Tiempo completo'}
              </Typography>
            </Box>
          </Card>
        ))}
      </Box>

      <Button 
        onClick={() => navigate(`/systems/bolsa/vacantes?userId=${userId}`)}
        sx={{ 
          width: '100%', 
          mt: 1, 
          textTransform: 'none', 
          fontWeight: '700',
          color: '#0a66c2',
          '&:hover': { bgcolor: '#eef3f8' }
        }}
        endIcon={<ArrowRight size={16} />}
      >
        Ver todas las vacantes
      </Button>
    </Card>
  );
};

export default FeaturedJobs;


